///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Info panel for a focused weather alert (NWS / Environment Canada /
/// MeteoAlarm). Leads with the severity badge and expiry countdown, then
/// the official headline; the full description and safety instructions
/// live in the expandable section.
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { Badge, formatTimeAgo, InfoField, MoreSection } from "@/components/controls/anchored-panel/panelAtoms"
import { type AlertProperties, type AlertSeverity } from "@/earthbrowser/dataset-distribution/alerts/AlertsTable"

export const cSeverityBadge: Record<AlertSeverity, string> = {
	Extreme: "bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/40",
	Severe: "bg-red-500/20 text-red-300 border border-red-400/40",
	Moderate: "bg-orange-500/20 text-orange-300 border border-orange-400/40",
	Minor: "bg-yellow-500/20 text-yellow-200 border border-yellow-400/40",
	Unknown: "bg-slate-500/20 text-slate-300 border border-slate-400/40",
}

const cSourceNames: Record<string, string> = {
	nws: "National Weather Service",
	eccc: "Environment Canada",
	meteoalarm: "MeteoAlarm",
}

export function AnchoredAlertInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "alert" ? data : null}
			fallbackTitle={content => content.alert.event}
			accentBorder="border-amber-400/20"
			pill="bg-amber-400/90"
			scrollable
		>
			{(content, ctx) => <AlertBody alert={content.alert} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

/** "Until Tue 5:30 PM" for upcoming expiry, or null when it has passed/is unset */
function untilLabel(expires: string | null): string | null {
	if (!expires) return null
	const date = new Date(expires)
	if (isNaN(date.getTime()) || date.getTime() < Date.now()) return null
	return date.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })
}

function AlertBody({ alert, ctx }: { alert: AlertProperties; ctx: PanelShellContext }) {
	const severity: AlertSeverity = alert.severity in cSeverityBadge ? alert.severity : "Unknown"
	const until = untilLabel(alert.ends ?? alert.expires)
	const sent = new Date(alert.sent)

	return (
		<>
			<div className="space-y-1">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="text-xl font-bold text-white leading-tight">
						{ctx.title}
					</span>
					<Badge colorClass={cSeverityBadge[severity]}>
						{severity}
					</Badge>
				</div>
				{alert.area_desc && (
					<div className="text-sm text-slate-200 leading-snug">
						{alert.area_desc}
					</div>
				)}
			</div>

			{until && (
				<div className="space-y-1">
					<div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">In effect until</div>
					<div className="text-2xl font-light text-white leading-none">{until}</div>
				</div>
			)}

			{alert.headline && (
				<div className="text-xs text-slate-300 leading-snug">
					{alert.headline}
				</div>
			)}

			<MoreSection compact={ctx.compact}>
				{(alert.description || alert.instruction) && (
					<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
						{alert.description && (
							<div className="text-xs text-slate-300 leading-relaxed whitespace-pre-line">
								{alert.description}
							</div>
						)}
						{alert.instruction && (
							<div className="text-xs text-amber-200/90 leading-relaxed whitespace-pre-line border-t border-white/5 pt-2">
								{alert.instruction}
							</div>
						)}
					</div>
				)}

				<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
					<div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
						<InfoField label="Issued" value={formatTimeAgo(sent)} />
						{alert.urgency && <InfoField label="Urgency" value={alert.urgency} />}
						{alert.certainty && <InfoField label="Certainty" value={alert.certainty} />}
						{alert.response_type && <InfoField label="Response" value={alert.response_type} />}
					</div>

					<div className="text-[10px] text-slate-500 pt-1 border-t border-white/5">
						{alert.sender_name ?? cSourceNames[alert.source] ?? alert.source}
						{alert.web_url && (
							<>
								{" "}&bull;{" "}
								<a
									href={alert.web_url}
									target="_blank"
									rel="noopener noreferrer"
									className="underline underline-offset-2 hover:text-slate-300"
								>
									Details
								</a>
							</>
						)}
					</div>
				</div>
			</MoreSection>
		</>
	)
}
